import React, { useEffect, useState } from "react";
import jsPDF from "jspdf";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import DateConversion from "../../../../../utils/DateConversion";
import CommaConvert from "../../../../../utils/CommaConvert";
import {
  formatAmountComma,
  parseAmountNumber,
} from "../../../../../utils/decimalAmountUtils";
import { useSelector } from "react-redux";
import {
  consolidateTrainingExpenseRowsForDisplay,
  filterInstallationOngoingOtherExpenseDisplayRows,
  isTrainingExpenseDisplayRow,
} from "../../../../../utils/trainingOtherExpenseSync";
import {
  canonicalOtherExpenseDescriptionLabel,
} from "../../../../../utils/projectAssignDescriptionMerge";

const isHardwareTypeService = (typeService) =>
  String(typeService || "").toLowerCase().includes("hardware");

const normalizeOtherExpenseDescription = (description) =>
  String(description ?? "").trim();

const sumOpExpensesForSelfManagerInput = (opEntries) =>
  (opEntries || []).reduce((sum, e) => sum + (parseAmountNumber(e?.expenses) || 0), 0);

const getSelfConsolidatedDisplayExpenses = (allEntries, managerEmpId) => {
  const managerId = String(managerEmpId || "");
  const entries = allEntries || [];
  const ownerEntry = entries.find(
    (e) => String(e?.taskownerid || "") === managerId
  );
  const managerExpenses = parseAmountNumber(ownerEntry?.expenses);
  const opEntries = entries.filter(
    (e) => String(e?.taskownerid || "") !== managerId
  );
  return managerExpenses + sumOpExpensesForSelfManagerInput(opEntries);
};

function buildHardwareManagerSelfOtherExpenses(allDescriptions = [], managerEmpId) {
  const managerId = String(managerEmpId || "");
  const nonTraining = (allDescriptions || []).filter(
    (entry) => !isTrainingExpenseDisplayRow(entry)
  );

  const groupedEntries = Object.values(
    nonTraining.reduce((acc, entry) => {
      const desc = normalizeOtherExpenseDescription(entry?.description);
      if (!desc) return acc;

      if (!acc[desc]) {
        acc[desc] = { value: 0, allEntries: [] };
      }
      acc[desc].value += parseAmountNumber(entry?.value) || 0;
      acc[desc].allEntries.push(entry);
      return acc;
    }, {})
  );

  return groupedEntries.map((group) => {
    const ownerEntry = group.allEntries.find(
      (e) => String(e?.taskownerid || "") === managerId
    );
    const pickedEntry = ownerEntry || group.allEntries[0];
    const displayExpenses = getSelfConsolidatedDisplayExpenses(
      group.allEntries,
      managerEmpId
    );

    return {
      ...pickedEntry,
      description: canonicalOtherExpenseDescriptionLabel(pickedEntry.description),
      value: group.value,
      expenses: displayExpenses,
      isTaskComplete: group.allEntries.every((e) => e?.isTaskComplete),
    };
  });
}

const ShowTaskDetailsCompleted = ({
  row,
  employees,
  projects,
  projectTrackingWorkEntries,
  descriptionProjectTracking,
  hardwareProjectTracking,
  entries,
  tasks,
  showListState,
  isViewOnly = false,
}) => {
  const [matchedProjects, setMatchedProjects] = useState([]);
  const currency = useCurrencySymbol();
  const { formatDateByCountry } = DateConversion();
  const USAComma = CommaConvert();
  const formatHours = (value) =>
    new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(Number(value) || 0);
  const hasExceededAssignedHours = (workEntries = []) =>
    workEntries.some(
      (entry) => Number(entry?.hoursspent || 0) > Number(entry?.hoursassigned || 0)
    );

  const { mytrainings } = useSelector((state) => state.myTrainings);
  const getTrainingRow = React.useCallback(
    (projectObj, emp) => {
      if (!projectObj || !emp) return null;
      const resolveRefId = (ref) => {
        if (ref == null || ref === "") return "";
        if (typeof ref === "object") return String(ref?._id ?? ref?.id ?? "").trim();
        return String(ref).trim();
      };
      const normKey = (v) => String(v ?? "").trim().toLowerCase();
      const pick = (...vals) => vals.find((v) => v != null && String(v).trim() !== "");
      const trainingsList = Array.isArray(mytrainings?.trainings)
        ? mytrainings.trainings
        : Array.isArray(mytrainings)
          ? mytrainings
          : [];

      const assigneeId = resolveRefId(emp?._id ?? emp?.id);
      const assigneeEmail = normKey(pick(emp?.EmpEmail, emp?.email, emp?.Email));
      if (!assigneeId && !assigneeEmail) return null;

      const leadLike = projectObj?.lead || projectObj?.Lead || null;
      const projectIdCandidates = new Set(
        [
          resolveRefId(pick(projectObj?._id, projectObj?.id, projectObj?.ProjectID, projectObj?.projectid, projectObj?.projectId)),
          resolveRefId(pick(leadLike?._id, leadLike?.id)),
        ]
          .map((x) => normKey(x))
          .filter(Boolean)
      );
      const quotationIdCandidates = new Set(
        [
          pick(projectObj?.quotationId, projectObj?.quotationID, projectObj?.quotationid),
          pick(leadLike?.quotationId, leadLike?.quotationID, leadLike?.quotationid),
        ]
          .map((x) => String(x || "").trim())
          .filter(Boolean)
      );
      const projectNameCandidates = new Set(
        [pick(projectObj?.ProjectName, projectObj?.projectname), pick(leadLike?.ProjectName, leadLike?.projectname)]
          .map((x) => normKey(x))
          .filter(Boolean)
      );
      const leadIdCandidates = new Set(
        [pick(projectObj?.leadid, projectObj?.leadId, projectObj?.LeadId, projectObj?.LeadID), pick(leadLike?.leadid, leadLike?.leadId, leadLike?.LeadId, leadLike?.LeadID)]
          .map((x) => String(x || "").trim())
          .filter(Boolean)
      );

      const matched = trainingsList.filter((t) => {
        const assignedToRef = t?.assignedto ?? t?.assignedTo;
        const assignedToId = resolveRefId(assignedToRef);
        const assignedToEmail =
          typeof assignedToRef === "string" && assignedToRef.includes("@")
            ? normKey(assignedToRef)
            : "";
        const matchesAssignee =
          (assigneeId && assignedToId && assignedToId === assigneeId) ||
          (assigneeEmail && assignedToEmail && assignedToEmail === assigneeEmail);
        if (!matchesAssignee) return false;

        const tProjectId = normKey(resolveRefId(t?.projectid ?? t?.projectId ?? t?.project));
        const tQuotationId = String(t?.quotationId ?? t?.quotationID ?? t?.quotationid ?? "").trim();
        const tProjectName = normKey(t?.projectname ?? t?.ProjectName);
        const tLeadId = String(t?.leadid ?? t?.leadId ?? t?.LeadId ?? "").trim();
        return (
          (tProjectId && projectIdCandidates.has(tProjectId)) ||
          (tQuotationId && quotationIdCandidates.has(tQuotationId)) ||
          (tProjectName && projectNameCandidates.has(tProjectName)) ||
          (tLeadId && leadIdCandidates.has(tLeadId))
        );
      });
      const normalized = matched.map((t) => {
        const amount = Number(String(t?.trainingcost ?? t?.trainingCost ?? 0).replace(/,/g, ""));
        const notes = t?.notes ?? t?.note ?? "";
        return { amount: Number.isFinite(amount) ? amount : 0, notes: String(notes || "").trim() };
      });
      const total = normalized.reduce((sum, x) => sum + (Number(x.amount) || 0), 0);
      const joinedNotes = Array.from(new Set(normalized.map((x) => x.notes).filter(Boolean))).join("; ");
      if (total <= 0 && !joinedNotes) return null;
      return {
        _id: `training_cost_${assigneeId || assigneeEmail}`,
        taskownerid: assigneeId || assigneeEmail,
        description: "Training Cost",
        value: total,
        notes: joinedNotes,
        isTaskComplete: true,
        expenses: total,
      };
    },
    [mytrainings]
  );

  useEffect(() => {
    if (projects?.length && row?.Projects?.length) {
      const matchedProjects = [];

      row.Projects.forEach((rowProject) => {
        const old = projects.find((pro) => {
          if (rowProject.quotationId != null && pro.quotationId != null) {
            return String(pro.quotationId) === String(rowProject.quotationId) && pro.TypeService === rowProject.TypeService;
          }
          return pro.TypeService === rowProject.TypeService && pro.ProjectName === rowProject.ProjectName;
        });

        if (old) {
          const isSame =
            old._id === rowProject.oldprojectid &&
            (old.quotationId != null && rowProject.quotationId != null ? String(old.quotationId) === String(rowProject.quotationId) : old.ProjectName === rowProject.ProjectName) &&
            old?.TypeService === rowProject?.TypeService;

          matchedProjects.push({
            _id: rowProject._id, // ✅ add row project id at top-level
            ProjectName: rowProject.ProjectName,
            TypeService: rowProject.TypeService, // ✅ add row type service
            oldProject: old,
            newProject: isSame ? old : rowProject, // replace with old if identical
          });
        }
      });

      setMatchedProjects(matchedProjects); // <-- store in your state
    }
  }, [projects, row?.Projects]);

  const [allrowData, setAllrowData] = useState([]);

  useEffect(() => {
    if (
      matchedProjects?.length &&
      (entries?.length ||
        tasks?.length ||
        projectTrackingWorkEntries?.length ||
        descriptionProjectTracking?.length)
    ) {
      const updated = matchedProjects?.map((proj) => {
        // old matches
        const oldworkEntries = entries.filter(
          (entry) => entry.projectid === proj.oldProject._id
        );
        const oldDescriptions = tasks.filter(
          (task) => task.projectid === proj.oldProject._id
        );

        // new matches
        const newworkEntries = projectTrackingWorkEntries.filter(
          (entry) => entry.projectid === proj.newProject._id
        );
        const newDescriptions = descriptionProjectTracking.filter(
          (desc) => desc.projectid === proj.newProject._id
        );

        const newHardwareParts = hardwareProjectTracking.filter(
          (desc) => desc.projectid === proj.newProject._id
        );

        return {
          _id: proj.newProject._id,
          TypeService: proj.newProject.TypeService,
          ProjectName: proj.newProject.ProjectName,
          Assignedperson: proj.newProject.Assignedperson,
          oldProject: {
            _id: proj.oldProject._id,
            ProjectName: proj.oldProject.ProjectName,
            TypeService: proj.oldProject.TypeService,
            Assignedperson: proj.oldProject.Assignedperson,
            oldworkEntries,
            oldDescriptions,
          },
          newProject: {
            _id: proj.newProject._id,
            ProjectName: proj.newProject.ProjectName,
            TypeService: proj.newProject.TypeService,
            Assignedperson: proj.newProject.Assignedperson,
            newworkEntries,
            newDescriptions,
            newHardwareParts: newHardwareParts,
          },
        };
      });

      setAllrowData(updated); // ✅ update state
    }
  }, [
    matchedProjects,
    entries,
    tasks,
    projectTrackingWorkEntries,
    descriptionProjectTracking,
    hardwareProjectTracking,
  ]);

  const [unifitedList, setUnifitedList] = useState([]);

  console.log("unifitedList", unifitedList);
  useEffect(() => {
    if (allrowData?.length && employees?.length) {
      const mergedData = allrowData?.map((project) => {
        const assignedData = project.newProject.Assignedperson?.map(
          (person) => {
            // 1️⃣ Find matching employee by email
            const emp = employees.find((e) => e.EmpEmail === person.email);

            if (!emp) return null; // skip if no match

            // 2️⃣ Collect role + id
            const empid = emp._id;
            const role = emp.customRoles?.[0] || null;
            const name = `${emp.EmpFirstName} ${emp.EmpLastName}`;

            // 3️⃣ Match old/new work entries and descriptions by taskownerid
            const oldworkEntries = project.oldProject.oldworkEntries.filter(
              (entry) => entry.taskownerid === empid
            );
            const newworkEntries = project.newProject.newworkEntries.filter(
              (entry) => entry.taskownerid === empid
            );

            const oldDescriptions = project.oldProject.oldDescriptions.filter(
              (desc) => desc.taskownerid === empid
            );
            // const newDescriptions = project.newProject.newDescriptions.filter(
            //   (desc) => desc.taskownerid === empid
            // );
            let newDescriptions = [];

            if (isHardwareTypeService(project.newProject?.TypeService)) {
              const isPM =
                role === "project manager" ||
                emp?.customRoles?.includes("project manager");
              if (isPM) {
                newDescriptions = buildHardwareManagerSelfOtherExpenses(
                  project.newProject?.newDescriptions || [],
                  empid
                );
              } else {
                newDescriptions = (project.newProject?.newDescriptions || []).filter(
                  (desc) => desc.taskownerid === empid
                );
              }
            } else {
              newDescriptions = (project.newProject?.newDescriptions || []).filter(
                (desc) => desc.taskownerid === empid
              );
            }

            // Trainings should show as a locked row in Other Expenses for each assignee (manager + operations).
            {
              const trainingRow = getTrainingRow(project?.newProject, emp);
              const hasTrainingAlready = (newDescriptions || []).some(
                (d) => isTrainingExpenseDisplayRow(d)
              );
              if (!hasTrainingAlready && trainingRow) {
                newDescriptions = [...newDescriptions, trainingRow];
              }
              newDescriptions = consolidateTrainingExpenseRowsForDisplay(newDescriptions);
            }
            newDescriptions = filterInstallationOngoingOtherExpenseDisplayRows(
              newDescriptions,
              project.newProject
            );
            const newHardwareParts = project.newProject.newHardwareParts.filter(
              (desc) => desc.taskownerid === empid
            );

            // 4️⃣ Build unified structure
            return {
              TypeService: project.TypeService,
              // Assignperson: project.Assignperson,
              name,
              empid,
              role,
              oldworkEntries,
              newworkEntries,
              oldDescriptions,
              newDescriptions,
              newHardwareParts,
            };
          }
        )
          .filter(Boolean) // remove nulls (non-matching employees)
          .sort((a, b) =>
            a.role === "project manager"
              ? -1
              : b.role === "project manager"
                ? 1
                : 0
          );

        if (isHardwareTypeService(project.newProject?.TypeService)) {

          // 1️⃣ Create employee map for quick lookup
          const empMap = assignedData.reduce((acc, e) => {
            acc[e.empid] = e;
            return acc;
          }, {});

          // 2️⃣ Collect ALL newHardwareParts
          const allNewHardwareParts = assignedData.flatMap(
            e => e.newHardwareParts || []
          );

          // 3️⃣ Group by assemblypartdescription
          const groupedByAssembly = allNewHardwareParts.reduce((acc, part) => {
            const key = part.assemblepartdescription;
            if (!acc[key]) acc[key] = [];
            acc[key].push(part);
            return acc;
          }, {});

          // 4️⃣ Reset processed hardware array
          assignedData.forEach(e => {
            e.hardwarePartsOngoing = [];
          });

          // 5️⃣ Find Project Manager
          const pmEmp = assignedData.find(
            e => e.role === "project manager"
          );

          // 6️⃣ Apply rules
          Object.entries(groupedByAssembly).forEach(([desc, parts]) => {
            if (!pmEmp) return;

            const pmParts = parts.filter(
              p => empMap[p.taskownerid]?.role === "project manager"
            );

            const opParts = parts.filter(
              p => empMap[p.taskownerid]?.role === "operations"
            );

            // ✅ Operations always keep their own parts
            opParts.forEach(part => {
              const opEmp = empMap[part.taskownerid];
              if (opEmp) opEmp.hardwarePartsOngoing.push(part);
            });

            // ✅ Decide PM entry
            if (pmParts.length && opParts.length) {

              const highestPM = pmParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              const highestOP = opParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push(
                Number(highestOP.quantity) > Number(highestPM.quantity)
                  ? { ...highestOP }
                  : { ...highestPM }
              );

            } else if (!pmParts.length && opParts.length) {
              // 🔥 OP only → clone to PM
              const highestOP = opParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push({ ...highestOP });

            } else if (pmParts.length) {

              const highestPM = pmParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push({ ...highestPM });
            }
          });

          // 7️⃣ OPTIONAL: replace original newHardwareParts with processed data
          assignedData.forEach(e => {
            e.newHardwareParts = e.hardwarePartsOngoing;
          });
        }

        return {
          ...project,
          AssignedPersonData: assignedData, // ✅ merged data per employee
        };
      });
      // store in state
      setUnifitedList(mergedData);
    }
  }, [allrowData, employees, getTrainingRow]);

  const downloadPDFHardware = (newHardwareParts) => {
    if (isViewOnly) return;
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "Sr. No",
          "Part Name",
          "Part Number",
          "Quantity",
          `Unit Price (${currency})`,
          `Total Price (${currency})`,
          "Start Date",
          "End Date",
        ],
      ],
      body: newHardwareParts?.map((LeadReport, index) => [
        index + 1,
        LeadReport.assemblepartdescription,
        LeadReport.partno,
        LeadReport.quantity,
        USAComma(LeadReport.unitprice),
        USAComma(LeadReport.totalprice),
        LeadReport.startdate ? formatDateByCountry(LeadReport.startdate) : "",
        LeadReport.enddate ? formatDateByCountry(LeadReport.enddate) : "",
      ]),
      styles: {
        fontSize: 7,
      },
      headStyles: {
        fontSize: 8,
      },
    });
    doc.save("Hardware PartList.pdf");
  };

  const downloadPDFHardwareDescriptions = (newDescriptions) => {
    if (isViewOnly) return;
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "Sr. No",
          "Description",
          `Amount (${currency})`,
          `Expenses (${currency})`,
          "Start Date",
          "End Date",
          "Notes",
        ],
      ],
      body: newDescriptions?.map((LeadReport, index) => [
        index + 1,
        LeadReport.description,
        formatAmountComma(LeadReport.value),
        formatAmountComma(LeadReport.expenses),
        LeadReport.startdate ? formatDateByCountry(LeadReport.startdate) : "",
        LeadReport.enddate ? formatDateByCountry(LeadReport.enddate) : "",
        LeadReport.notes,
      ]),
      styles: {
        fontSize: 7,
      },
      headStyles: {
        fontSize: 8,
      },
    });
    doc.save("Hardware OtherExpensesList.pdf");
  };

  // const [showActionToAssign, setShowActionToAssign] = useState(null);
  const [currentProject, setCurrentProject] = useState({});
  const [clickedIdPerson, setClickedIdPerson] = useState(null);

  useEffect(() => {
    if (!unifitedList?.length) return;

    // find the first project manager
    for (const project of unifitedList) {
      const manager = project?.AssignedPersonData?.find(
        (p) => p.role === "project manager"
      );
      if (manager) {
        setCurrentProject(manager);
        setClickedIdPerson(manager.empid);
        break; // stop once found
      }
    }
  }, [unifitedList]); // run when data loads

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      className="flex flex-col w-full"
    >
      <div className="w-full "></div>
      {unifitedList?.map((project, projectIndex) => (
        <div key={projectIndex} className="w-full flex border-b">
          <div className="w-full flex flex-col ">
            {project.AssignedPersonData?.map((proj, index) => {
              // Get all employees with "operations" role in this project
              const operationsEmployees =
                project.AssignedPersonData?.filter(
                  (p) => p.role === "operations"
                ) || [];

              // Find the index of current proj in operationsEmployees array
              const operationsIndex = operationsEmployees.findIndex(
                (p) => p.empid === proj.empid
              );

              // Determine display name based on role
              const displayName =
                proj.role === "operations"
                  ? operationsEmployees.length === 1
                    ? `${proj.TypeService} employee`
                    : `${proj.TypeService} employee ${operationsIndex + 1}`
                  : proj.name;
              const shouldHighlightPerson = hasExceededAssignedHours(
                proj?.newworkEntries
              );

              return (
                <div key={index} className="w-full flex ">
                  <div
                    onClick={() => {
                      setCurrentProject(currentProject === proj ? null : proj);
                      setClickedIdPerson(
                        clickedIdPerson === proj.empid ? null : proj.empid
                      );
                    }}
                    className="w-36 text-xs flex justify-start items-center border-b p-2 break-words text-wrap text-start"
                  >
                    <div
                      className={`${clickedIdPerson === proj.empid
                          ? "text-blue-500"
                          : shouldHighlightPerson
                            ? "text-red-500"
                            : ""
                        } px-2 py-1 rounded-md text-start`}
                    >
                      {proj.role === "operations" ? (
                        <span className="capitalize"> {displayName}</span>
                      ) : (
                        <>
                          <span className="capitalize"> {displayName}</span>
                          <br />
                          <span className="text-[11px]">
                            ({proj.TypeService}{" "}
                            {proj.role === "project manager"
                              ? "Manager"
                              : "Employee"}
                            )
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="w-[36rem] border-b p-2 break-words text-wrap">
                    {clickedIdPerson === proj.empid ? (
                      <>
                        {proj?.TypeService?.toLowerCase().includes("hardware")
                          ? // {proj.TypeService === "Hardware"
                          showListState === "tasktable" && (
                            <>
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                className="w-full rounded-lg h-full justify-center pb-5"
                              >
                                <div className="w-full border   overflow-hidden">
                                  <div className="grid grid-cols-3 bg-[#e8e8e8] text-[11px] font-semibold">
                                    <div className="p-1 border-l border-b  text-center">
                                      Total Parts{" "}
                                    </div>
                                    <div className="p-1 border-b  text-center">
                                      Total Price ({currency})
                                    </div>
                                    <div className="p-1 border-b  border-r text-center">
                                      Action
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-3 bg-white text-black text-[11px]">
                                    <div className="p-1 border-b border-l  text-center">
                                      {
                                        currentProject?.newHardwareParts
                                          ?.length
                                      }
                                    </div>
                                    <div className="p-1 border-b   text-center">
                                      {USAComma(
                                        currentProject?.newHardwareParts?.reduce(
                                          (sum, item) => {
                                            return (
                                              sum + (item.totalprice || 0)
                                            );
                                          },
                                          0
                                        )
                                      )}{" "}
                                    </div>
                                    <div
                                      title="Download Hardware Part List"
                                      className={`p-1 text-center border-b border-r ${isViewOnly ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-blue-50"}`}
                                      onClick={() =>
                                        downloadPDFHardware(
                                          currentProject?.newHardwareParts
                                        )
                                      }
                                    >
                                      Download
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          )
                          : showListState === "tasktable" && (
                            <div className="w-full flex  gap-2">
                              <div className="w-full max-h-[14rem] overflow-auto">
                                <div className="sticky top-0  text-[11px] text-center">
                                  <div className="flex bg-[#e8e8e8]">
                                    <div className="w-[21%] border-b border-l p-1 flex justify-center items-center align-middle">
                                      Task
                                    </div>
                                    <div className="w-[15%] border-b p-1 flex justify-center items-center align-middle">
                                      Hours Assigned
                                    </div>
                                    <div className="w-[15%] border-b p-1 flex justify-center items-center align-middle">
                                      Hours Spent
                                    </div>
                                    <div className="w-[17%] border-b p-1 flex justify-center items-center align-middle">
                                      Start Date
                                    </div>
                                    <div className="w-[17%] border-b p-1 flex justify-center items-center align-middle">
                                      End Date
                                    </div>
                                    <div className="w-[15%] border-b border-r p-1 flex justify-center items-center align-middle">
                                      Notes
                                    </div>
                                  </div>
                                  <div className="flex">
                                    <div className="w-[21%] border-b border-l p-1">
                                      Total
                                    </div>
                                    <div className="w-[15%] border-b  p-1">
                                      {formatHours(
                                        currentProject?.newworkEntries?.reduce(
                                          (sum, item) =>
                                            sum + item.hoursassigned,
                                          0
                                        ) || 0
                                      )}
                                    </div>
                                    <div className="w-[15%] border-b  p-1">
                                      {formatHours(
                                        currentProject?.newworkEntries?.reduce(
                                          (sum, item) =>
                                            sum + item.hoursspent,
                                          0
                                        ) || 0
                                      )}
                                    </div>
                                    <div className="w-[17%] border-b  p-1"></div>
                                    <div className="w-[17%] border-b  p-1"></div>
                                    <div className="w-[15%] border-b border-r p-1"></div>
                                  </div>
                                </div>
                                {currentProject?.newworkEntries?.map(
                                  (entry, index) => {
                                    const matchedOldWorkEntry = entries.find(
                                      (old) => old._id === entry.oldtaskid
                                    );
                                    return (
                                      <div
                                        key={index}
                                        className="flex text-center text-[11px]"
                                      >
                                        <div
                                          className="w-[21%] border-b  border-l p-1 max-h-14 overflow-auto"
                                          title={
                                            matchedOldWorkEntry
                                              ? `Previous Name: ${matchedOldWorkEntry.task}`
                                              : ""
                                          }
                                        >
                                          {entry.task}
                                        </div>
                                        <div
                                          className="w-[15%] border-b  p-1"
                                          title={
                                            matchedOldWorkEntry
                                              ? `Previous Hrs: ${matchedOldWorkEntry.hoursassigned}`
                                              : ""
                                          }
                                        >
                                          {entry.hoursassigned !== undefined &&
                                          entry.hoursassigned !== null &&
                                          entry.hoursassigned !== ""
                                            ? formatHours(entry.hoursassigned)
                                            : ""}
                                        </div>
                                        <div className="w-[15%] border-b  p-1">
                                          {entry.hoursspent
                                            ? formatHours(entry.hoursspent)
                                            : ""}
                                        </div>
                                        <div className="w-[17%] border-b  p-1 max-h-14 overflow-auto">
                                          {entry.startdate
                                            ? formatDateByCountry(
                                              entry.startdate
                                            )
                                            : ""}
                                        </div>
                                        <div className="w-[17%] border-b  p-1 max-h-14 overflow-auto">
                                          {entry.enddate
                                            ? formatDateByCountry(
                                              entry.enddate
                                            )
                                            : ""}
                                        </div>
                                        <div
                                          className="w-[15%] border-b border-r p-1 max-h-14 overflow-auto "
                                          title={
                                            matchedOldWorkEntry
                                              ? `Previous Notes: ${matchedOldWorkEntry.notes}`
                                              : ""
                                          }
                                        >
                                          {entry.notes}
                                        </div>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            </div>
                          )}

                        {showListState === "otherexpenses" &&
                          (currentProject?.TypeService?.toLowerCase().includes(
                            "hardware"
                          ) ? (
                            // (currentProject?.TypeService === "Hardware" ? (
                            <div className="w-full border  overflow-hidden">
                              <div className="grid grid-cols-4 bg-[#e8e8e8] text-[11px] font-semibold">
                                <div className="p-1 border-b border-l  text-center">
                                  Total Descriptions{" "}
                                </div>
                                <div className="p-1 border-b  text-center">
                                  Amount ({currency})
                                </div>
                                <div className="p-1 border-b  text-center">
                                  Expenses ({currency})
                                </div>
                                <div className="p-1 border-b  border-r text-center">
                                  Action
                                </div>
                              </div>

                              <div className="grid grid-cols-4 bg-white text-black text-[11px]">
                                <div className="p-1 border-b border-l text-center">
                                  {currentProject?.newDescriptions?.length}
                                </div>
                                <div className="p-1 border-b  text-center">
                                  {formatAmountComma(
                                    currentProject?.newDescriptions?.reduce(
                                      (sum, item) => {
                                        return sum + parseAmountNumber(item.value);
                                      },
                                      0
                                    )
                                  )}{" "}
                                </div>
                                <div className="p-1 border-b  text-center">
                                  {formatAmountComma(
                                    currentProject?.newDescriptions?.reduce(
                                      (sum, item) => {
                                        return sum + parseAmountNumber(item.expenses);
                                      },
                                      0
                                    )
                                  )}{" "}
                                </div>
                                <div
                                  title="Download Description List"
                                  className={`p-1 text-center border-b border-r ${isViewOnly ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-blue-50"}`}
                                  onClick={() =>
                                    downloadPDFHardwareDescriptions(
                                      currentProject?.newDescriptions
                                    )
                                  }
                                >
                                  Download
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full flex  gap-2">
                              <div className="w-full max-h-[14rem] overflow-auto">
                                <div className="sticky top-0  text-[11px] text-center">
                                  <div className="flex bg-[#e8e8e8]">
                                    {" "}
                                    <div className="w-[21%] border-b border-l p-1 truncate">
                                      Description
                                    </div>
                                    <div className="w-[15%] border-b  p-1">
                                      Amount ({currency})
                                    </div>
                                    <div className="w-[15%] border-b  p-1">
                                      Expenses ({currency})
                                    </div>
                                    <div className="w-[17%] border-b  p-1">
                                      Start Date
                                    </div>
                                    <div className="w-[17%] border-b  p-1">
                                      End Date
                                    </div>
                                    <div className="w-[15%] border-b border-r p-1">
                                      Notes
                                    </div>
                                  </div>

                                  <div className="flex">
                                    <div className="w-[21%] border-b border-l p-1">
                                      Total
                                    </div>
                                    <div className="w-[15%] border-b  p-1">
                                      {formatAmountComma(
                                        currentProject?.newDescriptions?.reduce(
                                          (sum, item) => sum + parseAmountNumber(item.value),
                                          0
                                        ) || 0
                                      )}
                                    </div>
                                    <div className="w-[15%] border-b  p-1">
                                      {formatAmountComma(
                                        currentProject?.newDescriptions?.reduce(
                                          (sum, item) => sum + parseAmountNumber(item.expenses),
                                          0
                                        ) || 0
                                      )}
                                    </div>
                                    <div className="w-[17%] border-b  p-1"></div>
                                    <div className="w-[17%] border-b  p-1"></div>
                                    <div className="w-[15%] border-b border-r p-1"></div>
                                  </div>
                                </div>
                                {currentProject?.newDescriptions?.map(
                                  (description, index) => {
                                    const matchedOldDescription = tasks.find(
                                      (old) => old._id === description.oldtaskid
                                    );
                                    return (
                                      <div
                                        key={index}
                                        className="flex text-center text-[11px]"
                                      >
                                        <div
                                          className="w-[21%] border-b border-l p-1 max-h-14 overflow-auto"
                                          title={
                                            matchedOldDescription
                                              ? `Previous Name: ${matchedOldDescription.description}`
                                              : ""
                                          }
                                        >
                                          {description.description}
                                        </div>
                                        <div
                                          className="w-[15%] border-b  p-1"
                                          title={
                                            matchedOldDescription
                                              ? `Previous Value: ${matchedOldDescription.value}`
                                              : ""
                                          }
                                        >
                                          {formatAmountComma(description.value)}
                                        </div>
                                        <div className="w-[15%] border-b  p-1">
                                          {formatAmountComma(description.expenses)}
                                        </div>
                                        <div className="w-[17%] border-b  p-1 max-h-14 overflow-auto">
                                          {description.startdate
                                            ? formatDateByCountry(
                                              description.startdate
                                            )
                                            : ""}
                                        </div>
                                        <div className="w-[17%] border-b  p-1 max-h-14 overflow-auto">
                                          {description.enddate
                                            ? formatDateByCountry(
                                              description.enddate
                                            )
                                            : ""}
                                        </div>
                                        <div
                                          className="w-[15%] border-b border-r p-1 max-h-14 overflow-auto"
                                          title={
                                            matchedOldDescription
                                              ? `Previous Note: ${matchedOldDescription.notes}`
                                              : ""
                                          }
                                        >
                                          {description.notes}
                                        </div>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            </div>
                          ))}
                      </>
                    ) : (
                      <div
                        onClick={() => {
                          setCurrentProject(
                            currentProject === proj ? null : proj
                          );
                          setClickedIdPerson(
                            clickedIdPerson === proj.empid ? null : proj.empid
                          );
                        }}
                        className="flex justify-center items-center h-full w-full cursor-pointer"
                      >
                        {" "}
                        Task Details
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ShowTaskDetailsCompleted;
